import axios from 'axios';
import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { loginRequest, initMsalInstance } from '../constants/authConfig';
import appConfig from '../constants/config';
import Dexie from 'dexie';

// ========== SETUP DEXIE DB ==========
const db = new Dexie('ApiCacheDB');
db.version(1).stores({
  cache: 'key,data,timestamp' // key = method+url+body hash, timestamp for TTL
});

// TTL in milliseconds (e.g., 1 hour)
const CACHE_TTL = 1000 * 60 * 60; 

// Utility: generate unique cache key
const getCacheKey = (method: string, url: string, data?: any) => {
  const body = data ? JSON.stringify(data) : '';
  return `${method}_${url}_${body}`;
};

// Utility: check if cache is expired
const isCacheValid = (timestamp: number) => {
  return Date.now() - timestamp < CACHE_TTL;
};

// ========== AXIOS INSTANCE ==========
const axiosInstance = axios.create();
let config: any = null;

const loadConfig = async () => {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/config.json?ts=${Date.now()}`);
    return await res.json();
  } catch (err) {
    console.error("Failed to load config.json", err);
    return null;
  }
};

// ========== API FUNCTIONS ==========
const getApiAxios = async (url: string, data: any, useCache: boolean = true) => {
  try {
    const apiResponse = await axiosInstance.get(url, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem("token"),
        contentType: 'application/json; charset=utf-8',
      },
      data: data,
      responseType: 'json'
    });
    return apiResponse.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

const postApiAxios = async (url: string, data: any, useCache: boolean = true) => {
  try {
    const apiResponse = await axiosInstance.post(url, data, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem("token"),
        contentType: 'application/json; charset=utf-8',
      },
    });
    return apiResponse.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

const postApiAxiosMultipartFormData = async (url: string, data: any) => {
  try {
    const apiResponse = await axiosInstance.post(url, data, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem("token"),
        contentType: 'multipart/form-data',
      },
    });
    return apiResponse.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const deleteApiAxios = async (url: string, data?: URLSearchParams) => {
  try {
    const config = {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem("token"),
        contentType: 'application/json; charset=utf-8',
      },
      data: data
    };
    const apiResponse = await axiosInstance.delete(url, config);
    return apiResponse.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const putApiAxios = async (url: string, data: any, dataType: string = 'json') => {
  try {
    const apiResponse = await axiosInstance.put(url, data, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem("token"),
        contentType: 'application/json; charset=utf-8',
        dataType: dataType
      },
    });
    return apiResponse.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

// ========== AXIOS INTERCEPTORS ==========

// Request interceptor: token + maintenance
axiosInstance.interceptors.request.use(
  async (config) => {
    config.headers.Authorization = 'Bearer ' + localStorage.getItem("token");

    const configData = await loadConfig();
    if (configData?.maintenance) {
      localStorage.clear();
      localStorage.setItem("maintenanceStartDate", configData.maintenanceStartDate);
      localStorage.setItem("maintenanceEndDate", configData.maintenanceEndDate);
      window.location.href = `${window.location.origin}${appConfig.connectionString.appBaseName}/maintenance`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: 401 refresh + offline caching
axiosInstance.interceptors.response.use(
  async (response) => {
    try {
      const method = response.config.method?.toUpperCase() || 'GET';
      if (method === 'GET' || method === 'POST') {
        const cacheKey = getCacheKey(method, response.config.url!, response.config.data ? JSON.parse(response.config.data) : undefined);
        await db.table('cache').put({ key: cacheKey, data: response.data, timestamp: Date.now() });
      }
    } catch (err) {
      console.warn("Failed to cache response:", err);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ----------- OFFLINE FALLBACK -----------
    if (!navigator.onLine || error.code === 'ERR_NETWORK') {
      const method = originalRequest.method?.toUpperCase() || 'GET';
      if (method === 'GET' || method === 'POST') {
        const cacheKey = getCacheKey(method, originalRequest.url!, originalRequest.data ? JSON.parse(originalRequest.data) : undefined);
        const cached = await db.table('cache').get(cacheKey);
        if (cached && isCacheValid(cached.timestamp)) {
          console.warn(`[Offline] Returning cached data for ${cacheKey}`);
          return {
            ...error.response,
            data: cached.data,
            status: 200,
            statusText: 'OK (from cache)',
            config: originalRequest
          };
        }
      }
    }

    // ----------- TOKEN REFRESH -----------
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshToken();
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ========== TOKEN REFRESH ==========
const refreshToken = async () => {
  const msalInstance = initMsalInstance();
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return msalInstance.acquireTokenRedirect(loginRequest);
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });
    localStorage.setItem("token", tokenResponse.accessToken);
    return tokenResponse.accessToken;
  } catch (error) {
    // Silent renewal uses a hidden iframe, which can fail for reasons other
    // than "interaction required" (e.g. monitor_window_timeout when a
    // browser's tracking prevention / third-party storage blocking prevents
    // the iframe from reaching login.microsoftonline.com). Treat any MSAL
    // auth error as needing interactive login so the app can recover
    // instead of getting stuck with failing API calls.
    if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
      return msalInstance.acquireTokenRedirect(loginRequest);
    } else {
      throw error;
    }
  }
};

export { getApiAxios, postApiAxios, postApiAxiosMultipartFormData };